import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, fmtMoney, fmtPct } from "@/lib/api";
import PageHeader, { Card } from "@/components/PageHeader";
import { Trophy, TrendingDown, Clock, AlertCircle } from "lucide-react";

const Stat = ({ label, value, accent }) => (
  <Card className="p-5">
    <div className="text-xs uppercase tracking-wider text-muted-foreground font-semibold">{label}</div>
    <div className={`font-display text-2xl md:text-3xl font-bold mt-1 ${accent || ""}`}>{value}</div>
  </Card>
);

export default function Insights() {
  const [d, setData] = useState(null);
  useEffect(() => { api.get("/insights").then((r) => setData(r.data)); }, []);
  if (!d) return <div className="text-sm text-muted-foreground">Loading…</div>;

  const t = d.totals;
  const profitAccent = t.total_profit >= 0 ? "text-emerald-600 dark:text-emerald-400" : "text-red-600 dark:text-red-400";

  return (
    <div data-testid="insights-page">
      <PageHeader overline="Business Intelligence" title="Insights & Analytics" description="Profitability, source performance, leaderboards, and aging." />

      {/* Totals */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6 stagger">
        <Stat label="Vehicles Purchased" value={t.purchased} />
        <Stat label="Vehicles Sold" value={t.sold} />
        <Stat label="In Stock" value={t.in_stock} />
        <Stat label="Total Revenue" value={fmtMoney(t.total_revenue)} />
        <Stat label="Total Profit" value={fmtMoney(t.total_profit)} accent={profitAccent} />
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6 stagger">
        <Stat label="Purchase Cost" value={fmtMoney(t.total_purchase_cost)} />
        <Stat label="Repair Cost" value={fmtMoney(t.total_repair_cost)} />
        <Stat label="Taxes Paid" value={fmtMoney(t.total_taxes_paid)} />
        <Stat label="Dealer Fees" value={fmtMoney(t.total_dealer_fees_paid)} />
        <Stat label="Avg Profit / Vehicle" value={fmtMoney(t.avg_profit_per_vehicle)} accent={profitAccent} />
      </div>

      {/* Analytics */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        <Card className="p-6">
          <div className="text-xs uppercase tracking-wider text-muted-foreground font-semibold mb-1">Top Performer</div>
          <div className="flex items-center gap-3 mb-3">
            <Trophy className="text-emerald-600" size={20} />
            <h3 className="font-display text-xl font-semibold">Most Profitable Vehicle</h3>
          </div>
          {d.analytics.most_profitable ? (
            <Link to={`/deal-sheet/${d.analytics.most_profitable.id}`} className="block group">
              <div className="font-display text-2xl font-bold group-hover:text-blue-600">{d.analytics.most_profitable.vehicle}</div>
              <div className="text-sm text-muted-foreground mt-1">{d.analytics.most_profitable.stock} · profit {fmtMoney(d.analytics.most_profitable.net_profit)} · ROI {fmtPct(d.analytics.most_profitable.roi_pct)}</div>
            </Link>
          ) : <div className="text-sm text-muted-foreground">No sales yet.</div>}
        </Card>

        <Card className="p-6">
          <div className="text-xs uppercase tracking-wider text-muted-foreground font-semibold mb-1">Underperformer</div>
          <div className="flex items-center gap-3 mb-3">
            <TrendingDown className="text-red-600" size={20} />
            <h3 className="font-display text-xl font-semibold">Least Profitable Vehicle</h3>
          </div>
          {d.analytics.least_profitable ? (
            <Link to={`/deal-sheet/${d.analytics.least_profitable.id}`} className="block group">
              <div className="font-display text-2xl font-bold group-hover:text-blue-600">{d.analytics.least_profitable.vehicle}</div>
              <div className="text-sm text-muted-foreground mt-1">{d.analytics.least_profitable.stock} · profit {fmtMoney(d.analytics.least_profitable.net_profit)} · ROI {fmtPct(d.analytics.least_profitable.roi_pct)}</div>
            </Link>
          ) : <div className="text-sm text-muted-foreground">No sales yet.</div>}
        </Card>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <Card className="p-5">
          <div className="flex items-center justify-between"><div className="text-xs uppercase tracking-wider text-muted-foreground font-semibold">Avg Days to Sell</div><Clock size={16} className="text-muted-foreground" /></div>
          <div className="font-display text-3xl font-bold mt-1">{Math.round(d.analytics.avg_days_to_sell)} d</div>
        </Card>
        <Card className="p-5">
          <div className="flex items-center justify-between"><div className="text-xs uppercase tracking-wider text-muted-foreground font-semibold">Held &gt; 60 days</div><AlertCircle size={16} className="text-amber-600" /></div>
          <div className="font-display text-3xl font-bold mt-1 text-amber-600">{d.analytics.held_over_60.length}</div>
        </Card>
        <Card className="p-5">
          <div className="flex items-center justify-between"><div className="text-xs uppercase tracking-wider text-muted-foreground font-semibold">Held &gt; 90 days</div><AlertCircle size={16} className="text-red-600" /></div>
          <div className="font-display text-3xl font-bold mt-1 text-red-600">{d.analytics.held_over_90.length}</div>
        </Card>
      </div>

      {/* Source performance */}
      <Card className="overflow-x-auto mb-6">
        <div className="p-5">
          <div className="text-xs uppercase tracking-wider text-muted-foreground font-semibold">Source Performance</div>
          <h3 className="font-display text-xl font-semibold mt-1">Auction vs Facebook</h3>
        </div>
        <table className="w-full text-sm">
          <thead className="bg-secondary text-xs uppercase tracking-wider text-muted-foreground">
            <tr>{["Source", "Vehicles", "Investment", "Revenue", "Profit", "Avg ROI"].map((h) => <th key={h} className="text-left px-4 py-3">{h}</th>)}</tr>
          </thead>
          <tbody>
            {d.source_performance.map((s) => (
              <tr key={s.source} className="border-t border-border">
                <td className="px-4 py-3 capitalize font-medium">{s.source}</td>
                <td className="px-4 py-3">{s.count}</td>
                <td className="px-4 py-3">{fmtMoney(s.investment)}</td>
                <td className="px-4 py-3">{fmtMoney(s.revenue)}</td>
                <td className={`px-4 py-3 font-semibold ${s.profit >= 0 ? "text-emerald-600" : "text-red-600"}`}>{fmtMoney(s.profit)}</td>
                <td className="px-4 py-3">{fmtPct(s.avg_roi)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>

      {/* Leaderboards */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {[
          { key: "by_profit", title: "Top 10 by Profit", metric: (r) => fmtMoney(r.net_profit), metricLabel: "Profit" },
          { key: "by_roi", title: "Top 10 by ROI", metric: (r) => fmtPct(r.roi_pct), metricLabel: "ROI" },
        ].map((board) => (
          <Card key={board.key} className="overflow-x-auto">
            <div className="p-5">
              <h3 className="font-display text-xl font-semibold">{board.title}</h3>
            </div>
            <table className="w-full text-sm">
              <thead className="bg-secondary text-xs uppercase tracking-wider text-muted-foreground">
                <tr>{["#", "Vehicle", "Source", board.metricLabel].map((h) => <th key={h} className="text-left px-4 py-3">{h}</th>)}</tr>
              </thead>
              <tbody>
                {d.leaderboard[board.key].map((r, i) => (
                  <tr key={r.id} className="border-t border-border">
                    <td className="px-4 py-3 font-mono text-xs">{i + 1}</td>
                    <td className="px-4 py-3"><Link to={`/deal-sheet/${r.id}`} className="font-medium hover:text-blue-600">{r.vehicle}</Link></td>
                    <td className="px-4 py-3 capitalize text-muted-foreground">{r.source || "—"}</td>
                    <td className="px-4 py-3 font-semibold">{board.metric(r)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>
        ))}
      </div>
    </div>
  );
}
